import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Paper, Box, Typography, Grid, Card, CardContent, Button, TextField,
  Autocomplete, Chip, CircularProgress, Alert, Tab, Tabs, Tooltip, IconButton
} from '@mui/material';
import {
  PictureAsPdf, TableChart, FilterList, Clear, Gavel,
  TrendingUp, TrendingDown, MonetizationOn, Assessment,
  DirectionsCar, Person, CalendarToday, CheckCircle,
  Warning, Error as ErrorIcon, Info
} from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const API_URL = process.env.REACT_APP_API_URL;

export default function RelatoriosMultas() {
  const [multas, setMultas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [filtros, setFiltros] = useState({
    data_inicial: '',
    data_final: '',
    cliente: null,
    veiculo: null,
    status: null
  });

  const statusOptions = [
    { value: 'pendente', label: 'Pendente', color: 'warning' },
    { value: 'pago_locadora', label: 'Pago pela Locadora', color: 'info' },
    { value: 'cobrado_cliente', label: 'Cobrado do Cliente', color: 'primary' },
    { value: 'recebido_cliente', label: 'Recebido do Cliente', color: 'success' },
    { value: 'contestada', label: 'Contestada', color: 'error' }
  ];

  // Carregar dados iniciais
  useEffect(() => {
    carregarClientes();
    carregarVeiculos();
    carregarMultas();
  }, []);

  // Recarregar quando filtros mudarem
  useEffect(() => {
    carregarMultas();
  }, [filtros]);

  const carregarClientes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/clientes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  const carregarVeiculos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/veiculos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVeiculos(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar veículos:', error);
    }
  };

  const carregarMultas = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/multas`;
      const params = new URLSearchParams();

      if (filtros.data_inicial) params.append('data_inicio', filtros.data_inicial);
      if (filtros.data_final) params.append('data_fim', filtros.data_final);
      if (filtros.cliente?.id) params.append('cliente_id', filtros.cliente.id);
      if (filtros.veiculo?.id) params.append('veiculo_id', filtros.veiculo.id);
      if (filtros.status?.value) params.append('status', filtros.status.value);

      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMultas(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar multas:', error);
    } finally {
      setLoading(false);
    }
  };

  // Estatísticas gerais
  const estatisticas = useMemo(() => {
    const total = multas.length;
    const pendentes = multas.filter(m => m.status === 'pendente').length;
    const pagasLocadora = multas.filter(m => m.status === 'pago_locadora').length;
    const cobradasCliente = multas.filter(m => m.status === 'cobrado_cliente').length;
    const recebidasCliente = multas.filter(m => m.status === 'recebido_cliente').length;
    const contestadas = multas.filter(m => m.status === 'contestada').length;

    const valorTotal = multas.reduce((acc, m) => acc + parseFloat(m.valor || 0), 0);
    const valorPendente = multas.filter(m => m.status === 'pendente').reduce((acc, m) => acc + parseFloat(m.valor || 0), 0);
    const valorPagoLocadora = multas.filter(m => m.status === 'pago_locadora').reduce((acc, m) => acc + parseFloat(m.valor || 0), 0);
    const valorRecebidoCliente = multas.filter(m => m.status === 'recebido_cliente').reduce((acc, m) => acc + parseFloat(m.valor || 0), 0);
    const saldoAPagar = valorPagoLocadora - valorRecebidoCliente;

    return {
      total,
      pendentes,
      pagasLocadora,
      cobradasCliente,
      recebidasCliente,
      contestadas,
      valorTotal,
      valorPendente,
      valorPagoLocadora,
      valorRecebidoCliente,
      saldoAPagar
    };
  }, [multas]);

  // Análise por veículo
  const analiseVeiculos = useMemo(() => {
    const veiculosComMultas = {};
    
    multas.forEach(multa => {
      const veiculoId = multa.veiculo_id;
      if (!veiculosComMultas[veiculoId]) {
        veiculosComMultas[veiculoId] = {
          veiculo_placa: multa.veiculo_placa || 'Não identificado',
          veiculo_modelo: multa.veiculo_modelo || '',
          quantidade: 0,
          valorTotal: 0,
          pendentes: 0,
          pagas: 0
        };
      }
      veiculosComMultas[veiculoId].quantidade++;
      veiculosComMultas[veiculoId].valorTotal += parseFloat(multa.valor || 0);
      if (multa.status === 'pendente') veiculosComMultas[veiculoId].pendentes++;
      if (multa.status === 'recebido_cliente') veiculosComMultas[veiculoId].pagas++;
    });

    return Object.values(veiculosComMultas).sort((a, b) => b.quantidade - a.quantidade);
  }, [multas]);

  // Análise por cliente
  const analiseClientes = useMemo(() => {
    const clientesComMultas = {};
    
    multas.forEach(multa => {
      const clienteId = multa.cliente_id;
      if (!clientesComMultas[clienteId]) {
        clientesComMultas[clienteId] = {
          cliente_nome: multa.cliente_nome || 'Não identificado',
          quantidade: 0,
          valorTotal: 0,
          pendentes: 0,
          pagas: 0
        };
      }
      clientesComMultas[clienteId].quantidade++;
      clientesComMultas[clienteId].valorTotal += parseFloat(multa.valor || 0);
      if (multa.status === 'pendente' || multa.status === 'cobrado_cliente') {
        clientesComMultas[clienteId].pendentes++;
      }
      if (multa.status === 'recebido_cliente') {
        clientesComMultas[clienteId].pagas++;
      }
    });

    return Object.values(clientesComMultas).sort((a, b) => b.quantidade - a.quantidade);
  }, [multas]);

  // Formatar valor
  const formatarValor = (valor) => {
    return `R$ ${parseFloat(valor || 0).toFixed(2).replace('.', ',')}`;
  };

  // Formatar data
  const formatarData = (data) => {
    return data ? new Date(data).toLocaleDateString('pt-BR') : '-';
  };

  // Limpar filtros
  const limparFiltros = () => {
    setFiltros({
      data_inicial: '',
      data_final: '',
      cliente: null,
      veiculo: null,
      status: null
    });
  };

  // Exportar para Excel
  const exportarExcel = () => {
    const dataAtual = new Date().toLocaleString('pt-BR');

    // ===== CABEÇALHO =====
    const cabecalho = [
      ['EASY LOCADORA'],
      ['RELATÓRIO DE MULTAS'],
      [`Gerado em: ${dataAtual}`],
      [''],
    ];

    // ===== FILTROS APLICADOS =====
    const periodo = filtros.data_inicial && filtros.data_final 
      ? `${formatarData(filtros.data_inicial)} a ${formatarData(filtros.data_final)}`
      : 'Todos os períodos';
    const veiculoFiltro = filtros.veiculo 
      ? `${filtros.veiculo.placa} - ${filtros.veiculo.modelo}`
      : 'Todos os veículos';
    const clienteFiltro = filtros.cliente 
      ? filtros.cliente.nome
      : 'Todos os clientes';
    const statusFiltro = filtros.status
      ? filtros.status.label
      : 'Todos os status';

    const filtrosAplicados = [
      ['FILTROS APLICADOS:'],
      [`Período: ${periodo}`],
      [`Veículo: ${veiculoFiltro}`],
      [`Cliente: ${clienteFiltro}`],
      [`Status: ${statusFiltro}`],
      [''],
    ];

    // ===== RESUMO ESTATÍSTICO =====
    const resumo = [
      ['RESUMO ESTATÍSTICO'],
      ['Total de Multas:', estatisticas.total],
      ['Pendentes:', estatisticas.pendentes],
      ['Pagas pela Locadora:', estatisticas.pagasLocadora],
      ['Cobradas do Cliente:', estatisticas.cobradasCliente],
      ['Recebidas do Cliente:', estatisticas.recebidasCliente],
      ['Contestadas:', estatisticas.contestadas],
      [''],
      ['VALORES'],
      ['Valor Total:', formatarValor(estatisticas.valorTotal)],
      ['Valor Pendente:', formatarValor(estatisticas.valorPendente)],
      ['Valor Pago Locadora:', formatarValor(estatisticas.valorPagoLocadora)],
      ['Valor Recebido Cliente:', formatarValor(estatisticas.valorRecebidoCliente)],
      ['Saldo a Receber:', formatarValor(estatisticas.saldoAPagar)],
      [''],
    ];

    // Top veículos/clientes
    if (analiseVeiculos.length > 0) {
      resumo.push(['Veículo com Mais Multas:', `${analiseVeiculos[0].veiculo_placa} (${analiseVeiculos[0].quantidade}x)`]);
    }
    if (analiseClientes.length > 0) {
      resumo.push(['Cliente com Mais Multas:', `${analiseClientes[0].cliente_nome} (${analiseClientes[0].quantidade}x)`]);
    }
    resumo.push(['']);

    // ===== DADOS DA TABELA =====
    const dadosHeader = ['Nº Auto', 'Descrição', 'Data Infração', 'Valor', 'Veículo', 'Cliente', 'Status'];
    const dadosBody = multas.map(m => [
      m.numero_auto || '',
      m.descricao || '',
      formatarData(m.data_infracao),
      formatarValor(m.valor),
      m.veiculo_placa || '',
      m.cliente_nome || '',
      statusOptions.find(s => s.value === m.status)?.label || m.status
    ]);

    const tabelaDados = [
      ['DADOS DAS MULTAS:'],
      dadosHeader,
      ...dadosBody
    ];

    // ===== MONTAGEM FINAL =====
    const dadosCompletos = [
      ...cabecalho,
      ...filtrosAplicados,
      ...resumo,
      ...tabelaDados
    ];

    const ws = XLSX.utils.aoa_to_sheet(dadosCompletos);

    // Larguras das colunas
    const colWidths = dadosHeader.map((_, idx) => {
      const maxLength = Math.max(
        dadosHeader[idx]?.length || 10,
        ...dadosBody.map(row => (row[idx]?.toString() || '').length)
      );
      return { wch: Math.min(maxLength + 2, 40) };
    });
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Multas');
    
    const dataArquivo = new Date().toISOString().split('T')[0];
    const nomeArquivo = `relatorio-multas-${dataArquivo}.xlsx`;
    const buffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    saveAs(new Blob([buffer]), nomeArquivo);
  };

  // Exportar para PDF
  const exportarPDF = () => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const dataAtual = new Date().toLocaleString('pt-BR');

    // ===== CABEÇALHO =====
    let yPos = 15;
    
    doc.setFillColor(31, 31, 31);
    doc.rect(0, 0, pageWidth, 25, 'F');
    
    doc.setTextColor(247, 139, 0);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('EASY LOCADORA', pageWidth / 2, 12, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text('RELATÓRIO DE MULTAS', pageWidth / 2, 20, { align: 'center' });

    yPos = 32;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Gerado em: ${dataAtual}`, pageWidth - 15, yPos, { align: 'right' });

    yPos = 40;

    // ===== FILTROS APLICADOS =====
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(247, 139, 0);
    doc.text('FILTROS APLICADOS:', 15, yPos);
    
    yPos += 6;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    
    const periodo = filtros.data_inicial && filtros.data_final 
      ? `${formatarData(filtros.data_inicial)} a ${formatarData(filtros.data_final)}`
      : 'Todos os periodos';
    doc.text(`• Periodo: ${periodo}`, 15, yPos);
    
    yPos += 5;
    const veiculoFiltro = filtros.veiculo 
      ? `${filtros.veiculo.placa} - ${filtros.veiculo.modelo}`
      : 'Todos os veiculos';
    doc.text(`• Veiculo: ${veiculoFiltro}`, 15, yPos);
    
    yPos += 5;
    const clienteFiltro = filtros.cliente 
      ? filtros.cliente.nome
      : 'Todos os clientes';
    doc.text(`• Cliente: ${clienteFiltro}`, 15, yPos);

    yPos += 5;
    const statusFiltro = filtros.status
      ? filtros.status.label
      : 'Todos os status';
    doc.text(`• Status: ${statusFiltro}`, 15, yPos);

    yPos += 10;

    // ===== RESUMO ESTATÍSTICO =====
    doc.setFillColor(247, 139, 0);
    doc.rect(15, yPos - 5, pageWidth - 30, 8, 'F');
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('RESUMO ESTATÍSTICO', pageWidth / 2, yPos, { align: 'center' });

    yPos += 10;
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');

    // Cards em colunas
    const col1X = 15;
    const col2X = pageWidth / 2 + 10;
    let col1Y = yPos;
    let col2Y = yPos;

    // Coluna 1
    doc.setFont('helvetica', 'bold');
    doc.text('Total de Multas:', col1X, col1Y);
    doc.setFont('helvetica', 'normal');
    doc.text(estatisticas.total.toString(), col1X + 40, col1Y);
    col1Y += 5;

    doc.setFont('helvetica', 'bold');
    doc.text('Pendentes:', col1X, col1Y);
    doc.setFont('helvetica', 'normal');
    doc.text(estatisticas.pendentes.toString(), col1X + 40, col1Y);
    col1Y += 5;

    doc.setFont('helvetica', 'bold');
    doc.text('Pagas pela Locadora:', col1X, col1Y);
    doc.setFont('helvetica', 'normal');
    doc.text(estatisticas.pagasLocadora.toString(), col1X + 40, col1Y);
    col1Y += 5;

    doc.setFont('helvetica', 'bold');
    doc.text('Recebidas do Cliente:', col1X, col1Y);
    doc.setFont('helvetica', 'normal');
    doc.text(estatisticas.recebidasCliente.toString(), col1X + 40, col1Y);
    col1Y += 5;

    // Coluna 2
    doc.setFont('helvetica', 'bold');
    doc.text('Valor Total:', col2X, col2Y);
    doc.setFont('helvetica', 'normal');
    doc.text(formatarValor(estatisticas.valorTotal), col2X + 40, col2Y);
    col2Y += 5;

    doc.setFont('helvetica', 'bold');
    doc.text('Valor Pendente:', col2X, col2Y);
    doc.setFont('helvetica', 'normal');
    doc.text(formatarValor(estatisticas.valorPendente), col2X + 40, col2Y);
    col2Y += 5;

    doc.setFont('helvetica', 'bold');
    doc.text('Valor Pago Locadora:', col2X, col2Y);
    doc.setFont('helvetica', 'normal');
    doc.text(formatarValor(estatisticas.valorPagoLocadora), col2X + 40, col2Y);
    col2Y += 5;

    doc.setFont('helvetica', 'bold');
    doc.text('Saldo a Receber:', col2X, col2Y);
    doc.setFont('helvetica', 'normal');
    doc.text(formatarValor(estatisticas.saldoAPagar), col2X + 40, col2Y);

    yPos = Math.max(col1Y, col2Y) + 8;

    // Top performers
    if (analiseVeiculos.length > 0 || analiseClientes.length > 0) {
      const topCol1X = 15;
      const topCol2X = pageWidth / 2 + 10;

      if (analiseVeiculos.length > 0) {
        doc.setFont('helvetica', 'bold');
        doc.text('Veiculo com Mais Multas:', topCol1X, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(`${analiseVeiculos[0].veiculo_placa} (${analiseVeiculos[0].quantidade}x)`, topCol1X + 50, yPos);
      }

      if (analiseClientes.length > 0) {
        doc.setFont('helvetica', 'bold');
        doc.text('Cliente com Mais Multas:', topCol2X, yPos);
        doc.setFont('helvetica', 'normal');
        const nomeCliente = analiseClientes[0].cliente_nome.substring(0, 30);
        doc.text(`${nomeCliente} (${analiseClientes[0].quantidade}x)`, topCol2X + 50, yPos);
      }

      yPos += 8;
    }

    // ===== TABELA DE DADOS =====
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(247, 139, 0);
    doc.text('DADOS DAS MULTAS:', 15, yPos);
    yPos += 2;

    const tableColumns = ['Nº Auto', 'Descrição', 'Data', 'Valor', 'Veículo', 'Cliente', 'Status'];
    const tableData = multas.slice(0, 50).map(m => [
      m.numero_auto || '',
      (m.descricao || '').substring(0, 35),
      formatarData(m.data_infracao),
      formatarValor(m.valor),
      m.veiculo_placa || '',
      (m.cliente_nome || '').substring(0, 20),
      statusOptions.find(s => s.value === m.status)?.label || m.status
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [tableColumns],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [247, 139, 0],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 7,
        cellPadding: 1.5
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: { left: 15, right: 15 },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 55 },
        2: { cellWidth: 22 },
        3: { cellWidth: 22 },
        4: { cellWidth: 22 },
        5: { cellWidth: 40 },
        6: { cellWidth: 35 }
      }
    });

    // Rodapé
    const finalY = doc.lastAutoTable.finalY || yPos + 50;
    if (finalY < pageHeight - 15) {
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'italic');
      doc.text(
        `Relatorio gerado automaticamente pelo sistema Easy Locadora - ${dataAtual}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    const dataArquivo = new Date().toISOString().split('T')[0];
    doc.save(`relatorio-multas-${dataArquivo}.pdf`);
  };

  // Colunas da tabela por aba
  const colunas = useMemo(() => {
    if (tabIndex === 0) {
      // Todas as multas
      return [
        {
          accessorKey: 'numero_auto',
          header: 'Nº Auto',
          size: 120
        },
        {
          accessorKey: 'descricao',
          header: 'Descrição',
          size: 200,
          Cell: ({ cell }) => (
            <Tooltip title={cell.getValue()}>
              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                {cell.getValue()}
              </Typography>
            </Tooltip>
          )
        },
        {
          accessorKey: 'data_infracao',
          header: 'Data Infração',
          size: 110,
          Cell: ({ cell }) => formatarData(cell.getValue())
        },
        {
          accessorKey: 'valor',
          header: 'Valor',
          size: 100,
          Cell: ({ cell }) => formatarValor(cell.getValue())
        },
        {
          accessorKey: 'veiculo_placa',
          header: 'Veículo',
          size: 100,
          Cell: ({ cell }) => cell.getValue() || 'N/A'
        },
        {
          accessorKey: 'cliente_nome',
          header: 'Cliente',
          size: 150,
          Cell: ({ cell }) => cell.getValue() || 'Não identificado'
        },
        {
          accessorKey: 'status',
          header: 'Status',
          size: 160,
          Cell: ({ cell }) => {
            const status = statusOptions.find(s => s.value === cell.getValue());
            return status ? (
              <Chip label={status.label} color={status.color} size="small" />
            ) : cell.getValue();
          }
        }
      ];
    } else if (tabIndex === 1) {
      // Por veículo
      return [
        {
          accessorKey: 'veiculo_placa',
          header: 'Placa',
          size: 100
        },
        {
          accessorKey: 'veiculo_modelo',
          header: 'Modelo',
          size: 150
        },
        {
          accessorKey: 'quantidade',
          header: 'Quantidade',
          size: 100,
          Cell: ({ cell }) => (
            <Chip label={cell.getValue()} color="primary" size="small" />
          )
        },
        {
          accessorKey: 'pendentes',
          header: 'Pendentes',
          size: 100,
          Cell: ({ cell }) => (
            <Chip label={cell.getValue()} color="warning" size="small" />
          )
        },
        {
          accessorKey: 'pagas',
          header: 'Pagas',
          size: 100,
          Cell: ({ cell }) => (
            <Chip label={cell.getValue()} color="success" size="small" />
          )
        },
        {
          accessorKey: 'valorTotal',
          header: 'Valor Total',
          size: 120,
          Cell: ({ cell }) => (
            <Typography fontWeight="bold" color="error">
              {formatarValor(cell.getValue())}
            </Typography>
          )
        }
      ];
    } else {
      // Por cliente
      return [
        {
          accessorKey: 'cliente_nome',
          header: 'Cliente',
          size: 200
        },
        {
          accessorKey: 'quantidade',
          header: 'Quantidade',
          size: 100,
          Cell: ({ cell }) => (
            <Chip label={cell.getValue()} color="primary" size="small" />
          )
        },
        {
          accessorKey: 'pendentes',
          header: 'Pendentes',
          size: 100,
          Cell: ({ cell }) => (
            <Chip label={cell.getValue()} color="warning" size="small" />
          )
        },
        {
          accessorKey: 'pagas',
          header: 'Pagas',
          size: 100,
          Cell: ({ cell }) => (
            <Chip label={cell.getValue()} color="success" size="small" />
          )
        },
        {
          accessorKey: 'valorTotal',
          header: 'Valor Total',
          size: 120,
          Cell: ({ cell }) => (
            <Typography fontWeight="bold" color="error">
              {formatarValor(cell.getValue())}
            </Typography>
          )
        }
      ];
    }
  }, [tabIndex]);

  // Dados da tabela por aba
  const dadosTabela = useMemo(() => {
    if (tabIndex === 0) return multas;
    if (tabIndex === 1) return analiseVeiculos;
    return analiseClientes;
  }, [tabIndex, multas, analiseVeiculos, analiseClientes]);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* HEADER */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Typography variant="h5" sx={{ fontWeight: "bold", color: "#f78b00" }}>
            📊 Relatórios de Multas
          </Typography>
          
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              color="error"
              startIcon={<PictureAsPdf />}
              onClick={exportarPDF}
              disabled={multas.length === 0}
            >
              Exportar PDF
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<TableChart />}
              onClick={exportarExcel}
              disabled={multas.length === 0}
            >
              Exportar Excel
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* FILTROS */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <FilterList color="primary" />
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Filtros
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Data Inicial"
              type="date"
              fullWidth
              size="small"
              value={filtros.data_inicial}
              onChange={(e) => setFiltros({ ...filtros, data_inicial: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Data Final"
              type="date"
              fullWidth
              size="small"
              value={filtros.data_final}
              onChange={(e) => setFiltros({ ...filtros, data_final: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={veiculos}
              getOptionLabel={(option) => `${option.placa} - ${option.modelo}`}
              value={filtros.veiculo}
              onChange={(e, newValue) => setFiltros({ ...filtros, veiculo: newValue })}
              renderInput={(params) => (
                <TextField {...params} label="Veículo" size="small" placeholder="Selecione..." />
              )}
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={clientes}
              getOptionLabel={(option) => option.nome}
              value={filtros.cliente}
              onChange={(e, newValue) => setFiltros({ ...filtros, cliente: newValue })}
              renderInput={(params) => (
                <TextField {...params} label="Cliente" size="small" placeholder="Selecione..." />
              )}
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={statusOptions}
              getOptionLabel={(option) => option.label}
              value={filtros.status}
              onChange={(e, newValue) => setFiltros({ ...filtros, status: newValue })}
              renderInput={(params) => (
                <TextField {...params} label="Status" size="small" placeholder="Selecione..." />
              )}
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<Clear />}
              onClick={limparFiltros}
              fullWidth
              sx={{ height: '40px' }}
            >
              Limpar Filtros
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* ESTATÍSTICAS */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: 2, borderTop: '4px solid #2196f3' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total de Multas
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {estatisticas.total}
                  </Typography>
                </Box>
                <Gavel sx={{ fontSize: 48, color: '#2196f3', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: 2, borderTop: '4px solid #ff9800' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Pendentes
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {estatisticas.pendentes}
                  </Typography>
                </Box>
                <Warning sx={{ fontSize: 48, color: '#ff9800', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: 2, borderTop: '4px solid #4caf50' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Recebidas
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {estatisticas.recebidasCliente}
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 48, color: '#4caf50', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: 2, borderTop: '4px solid #f44336' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Valor Total
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="error.main">
                    {formatarValor(estatisticas.valorTotal)}
                  </Typography>
                </Box>
                <MonetizationOn sx={{ fontSize: 48, color: '#f44336', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Valor Pendente
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="warning.main">
                {formatarValor(estatisticas.valorPendente)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Pago pela Locadora
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="info.main">
                {formatarValor(estatisticas.valorPagoLocadora)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Saldo a Receber
              </Typography>
              <Typography variant="h6" fontWeight="bold" color={estatisticas.saldoAPagar > 0 ? 'error.main' : 'success.main'}>
                {formatarValor(estatisticas.saldoAPagar)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ALERTAS */}
      {estatisticas.pendentes > 0 && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          <Typography variant="body2">
            <strong>Atenção:</strong> Existem {estatisticas.pendentes} multa(s) pendente(s) no valor total de {formatarValor(estatisticas.valorPendente)}.
          </Typography>
        </Alert>
      )}

      {estatisticas.saldoAPagar > 0 && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          <Typography variant="body2">
            <strong>Importante:</strong> A locadora possui um saldo de {formatarValor(estatisticas.saldoAPagar)} a receber de clientes.
          </Typography>
        </Alert>
      )}

      {/* TABELAS */}
      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
        <Tabs
          value={tabIndex}
          onChange={(e, newValue) => setTabIndex(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        >
          <Tab label="Todas as Multas" icon={<Gavel />} iconPosition="start" />
          <Tab label="Por Veículo" icon={<DirectionsCar />} iconPosition="start" />
          <Tab label="Por Cliente" icon={<Person />} iconPosition="start" />
        </Tabs>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : (
          <MaterialReactTable
            columns={colunas}
            data={dadosTabela}
            localization={MRT_Localization_PT_BR}
            enableColumnOrdering
            enableSorting
            enablePagination
            enableColumnFilters
            enableGlobalFilter
            enableDensityToggle
            initialState={{
              density: 'compact',
              pagination: { pageSize: 10 }
            }}
            muiTablePaperProps={{
              elevation: 0,
              sx: { borderRadius: 0 }
            }}
            muiTableHeadCellProps={{
              sx: {
                backgroundColor: '#f5f5f5',
                fontWeight: 'bold',
                color: '#333'
              }
            }}
          />
        )}
      </Paper>
    </Container>
  );
}
